import { ClerkProvider } from '@clerk/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { SWRConfig, type Cache } from 'swr';
import './index.css';
import App from './App.tsx';
import { clerkAppearance } from './clerkAppearance';
import AutoActivateSingleOrg from './components/AutoActivateSingleOrg';
import CatalogLiveAuth from './components/CatalogLiveAuth';
import TicketSignIn from './components/TicketSignIn';
import { initFaro } from './observability/faro';
import { reloadForStaleChunk } from './utils/staleChunk';
import { createPersistentSWRCache } from './utils/swrCache';

// Start frontend observability before render so early errors are captured.
// No-op unless VITE_GRAFANA_FARO_URL is set.
initFaro();

// A deploy replaces the hashed /assets chunks, so an already-open tab 404s
// the old hashes on its next lazy route load. Vite surfaces that failure as
// 'vite:preloadError'; reload once (loop-guarded) to pick up the fresh
// index.html instead of dropping the user on the error screen.
window.addEventListener('vite:preloadError', event => {
  if (reloadForStaleChunk()) event.preventDefault();
});

const persistentCache = createPersistentSWRCache();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    const register = (): void => {
      void navigator.serviceWorker.register('/sw.js').catch(() => {});
    };
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    if (typeof idle === 'function') idle(register);
    else setTimeout(register, 2000);
  });
}

// The hosted `/embed/*` routes are chrome-less, auth-less, and loaded inside
// third-party iframes. They don't use Clerk, and booting Clerk's frontend
// handshake inside a frame stalls in browsers with strict framed-content cookie
// policies (Safari/Brave/partitioned storage), leaving the embed blank. So mount
// the embed tree without Clerk; everything else keeps the full provider stack.
// Every hosted embed lives under `/embed/` so this stay-in-sync rule is one
// prefix, not a kind list.
const isEmbed = /^\/embed\//.test(window.location.pathname);

const swrValue = {
  revalidateOnFocus: false,
  dedupingInterval: 10000,
  errorRetryCount: 3,
  loadingTimeout: 3000,
  provider: () => persistentCache as unknown as Cache,
};

const appTree = (
  <SWRConfig value={swrValue}>
    <App />
  </SWRConfig>
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {isEmbed ? (
      appTree
    ) : (
      <ClerkProvider
        publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
        afterSignOutUrl="/"
        appearance={clerkAppearance}
      >
        <TicketSignIn />
        <AutoActivateSingleOrg />
        <SWRConfig value={swrValue}>
          <CatalogLiveAuth />
          <App />
        </SWRConfig>
      </ClerkProvider>
    )}
  </StrictMode>
);
